import type { ComponentPropsWithRef, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { type Tone, toneClasses } from "./tone";

/**
 * How round a surface is, which follows what it is rather than how big.
 *
 * Two, because the tree had a dozen: `md` drew forty-two of the hundred and
 * four surfaces and `lg` thirty-five, and the rest were hand-tuned values that
 * no rule chose.
 *
 * The scale is this application's, not Tailwind's: `index.css` sets `--radius-md`
 * to 12px and `--radius-lg` to 16px, against the framework's 6 and 8. A reader
 * who takes the class names for their defaults will misjudge every conversion
 * here by six pixels or more.
 */
const SHAPE = {
  /** A card, a panel, a popover: something that holds a piece of the page. */
  card: "rounded-lg",
  /** A row of a list, a note, a field: something inside one. */
  row: "rounded-md",
} as const;

/**
 * Three steps, from the sixteen the cards alone had between them, and none for
 * a surface whose rows pad themselves.
 */
const PAD = {
  none: "",
  /** A row of a dense list, a line high beside its controls: the framework's subsections. */
  line: "px-3 py-1.5",
  tight: "px-3 py-2",
  snug: "px-3.5 py-2.5",
  roomy: "p-4",
  /** A card that is the whole of a page's content, standing alone in its column: a locked stage's. */
  ample: "p-7",
} as const;

const ROOMINESS: Record<keyof typeof SHAPE, keyof typeof PAD> = {
  card: "roomy",
  row: "snug",
};

/**
 * How far a surface stands off the page. A panel sits flat on it; a popover a
 * form is written in is lifted further than a menu, as the design lifts its
 * feedback panel, and drops in under the control that opened it.
 */
const LIFT = {
  form: "shadow-popover-form animate-pop-in",
} as const;

/** The elements a surface is drawn as. A row of a list is an `li`, a form a `form`. */
type PanelTag =
  | "div"
  | "section"
  | "article"
  | "aside"
  | "figure"
  | "form"
  | "ul"
  | "ol"
  | "li"
  | "p"
  // A tooltip is a surface that has to sit in a trigger's inline flow.
  | "span";

type Shell = {
  shape?: keyof typeof SHAPE;
  pad?: keyof typeof PAD;
  lift?: keyof typeof LIFT;
  /**
   * A surface still waiting on the attorney: the warning edge and a faint ring round it. A
   * prompt rather than a gate, so the surface stays usable as it is.
   */
  attention?: boolean;
  /**
   * Something set aside, waiting to be restored or removed for good: a dashed
   * edge on the sunken ground, as the design draws a deleted draft.
   */
  setAside?: boolean;
  /** A rule down the left side in a tone, for a surface that reads at a glance as one state of several. */
  edge?: Tone;
  /** A violet rule along the top, as v0.28 caps the card that hands over a finished draft. */
  cap?: boolean;
  children?: ReactNode;
} & (
  | {
      /**
       * A tinted surface, for a panel that says how something is doing; or
       * `brand`, the product's own ground, for a small form the attorney fills
       * in inside a row. Brand is drawn from the theme's border and muted
       * tokens, so it adds none, and it is not a tone because it says nothing
       * about state.
       */
      tone?: Tone | "brand";
      outline?: never;
    }
  | {
      /** A tone's border on the card's own ground: the tint is left to what the card holds. */
      outline?: Tone;
      tone?: never;
    }
);

/**
 * Generic over the tag rather than a union of them, because a ref has to know
 * what it holds: a single `Ref<HTMLElement>` cannot satisfy `div` and `li` at
 * once, and several surfaces are measured or scrolled to.
 */
export type PanelProps<T extends PanelTag = "div"> = Shell & { as?: T } & Omit<
    ComponentPropsWithRef<T>,
    keyof Shell | "as"
  >;

/**
 * A bordered surface: the shell every card, row and panel is drawn on.
 *
 * The shape and the padding are named rather than passed as classes, because
 * naming them is the whole point: a hundred and four surfaces had a dozen radii
 * between them, and no two authors could have agreed on which to reach for. `className` is still open, for what a surface says about its
 * own state: a left edge in the tag's colour, a ring once it is settled.
 */
export function Panel<T extends PanelTag = "div">({
  as,
  shape = "card",
  pad,
  tone,
  outline,
  lift,
  attention = false,
  setAside = false,
  edge,
  cap = false,
  className,
  children,
  ...rest
}: PanelProps<T>) {
  // Widened at the point of use, so JSX is handed one component type rather
  // than asked to reconcile seven. The caller's tag is what typed the props
  // above, including its ref; a widened element cannot be asked to check them
  // again, because no single ref type satisfies `div` and `li` at once.
  const Element = (as ?? "div") as "div";
  const attributes = rest as ComponentPropsWithRef<"div">;
  return (
    <Element
      className={cn(
        "border",
        SHAPE[shape],
        PAD[pad ?? ROOMINESS[shape]],
        tone === undefined
          ? cn("bg-card", toneClasses(outline ?? "neutral", "line"))
          : tone === "brand"
            ? "border-border bg-muted"
            : toneClasses(tone, "surface"),
        lift !== undefined && LIFT[lift],
        attention && "border-warning ring-2 ring-warning/10",
        setAside && "border-dashed bg-surface-sunken",
        edge !== undefined && cn("border-l-4", toneClasses(edge, "edge")),
        cap && "border-t-[3px] border-t-primary",
        className,
      )}
      {...attributes}
    >
      {children}
    </Element>
  );
}
